/**
 * A patient's form request from a clinic website (2026-10-23).
 *
 * The page at /request/<slug>/<form_key> collects the minimum, checks it here,
 * and seals it in the browser under the clinic's Mac public key before it
 * leaves (bridge/routers/access_requests.py stores only the ciphertext).
 * Pure functions, so the rules are testable without a browser.
 */

export interface AccessRequestInput {
  firstName: string
  lastName: string
  dob: string
  email: string
  phone: string
  code: string
  currentPatient: boolean
}

export interface AccessRequestPayload {
  patient: { first_name: string; last_name: string; dob: string; email?: string; phone?: string }
  code?: string
  current_patient: boolean
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const DATE = /^\d{4}-\d{2}-\d{2}$/

/** The first problem with the input, in the words the page shows, or null. */
export function accessRequestProblem(input: AccessRequestInput, today: Date = new Date(),
                                     codeRequired = false, codeLabel = 'the code'): string | null {
  if (!input.firstName.trim() || !input.lastName.trim()) return 'Enter your first and last name.'
  if (!DATE.test(input.dob)) return 'Enter your date of birth.'
  const dob = new Date(`${input.dob}T00:00:00`)
  if (Number.isNaN(dob.getTime()) || dob > today || dob.getFullYear() < 1900) {
    return 'Check your date of birth.'
  }
  const email = input.email.trim()
  const phoneDigits = input.phone.replace(/\D/g, '')
  if (!email && !phoneDigits) return 'Enter an email address or a mobile number so the office can send your link.'
  if (email && !EMAIL.test(email)) return 'Check your email address.'
  if (phoneDigits && (phoneDigits.length < 10 || phoneDigits.length > 11)) return 'Check your mobile number.'
  if (codeRequired && !input.code.trim()) return `Enter ${codeLabel}.`
  if (input.code.trim().length > 64) return 'That code is longer than expected. Check it and try again.'
  if (!input.currentPatient) return 'This form is only for current patients of the clinic.'
  return null
}

/** What gets sealed: trimmed, and only what was entered. */
export function accessRequestPayload(input: AccessRequestInput): AccessRequestPayload {
  const patient: AccessRequestPayload['patient'] = {
    first_name: input.firstName.trim(),
    last_name: input.lastName.trim(),
    dob: input.dob,
  }
  const email = input.email.trim()
  const phone = input.phone.trim()
  if (email) patient.email = email
  if (phone) patient.phone = phone
  const out: AccessRequestPayload = { patient, current_patient: input.currentPatient }
  const code = input.code.trim()
  if (code) out.code = code
  return out
}
